import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Alert, Box, Breadcrumbs, Button, CircularProgress, Grid, Link, Paper, Stack, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { format } from "date-fns";

import API from "../../utils/Axios.utils";
import useOffers from "../../hooks/useOffers";
import logger from "../../utils/logger";
import {
  OFFER_STATUS, hasOfferLetter, hasUnfilledPlaceholders, jobBudgetLabel, offerDocsState, offerStatusSummary,
} from "../../utils/offers";

const CHECK_ICONS = Object.freeze({
  ok: <CheckCircleIcon fontSize="small" color="success" />,
  warn: <WarningAmberIcon fontSize="small" color="warning" />,
  info: <InfoOutlinedIcon fontSize="small" color="info" />,
});

const formatDateTime = (value) => (value ? format(new Date(value), "dd MMM yyyy") : null);

/** The checks panel: what a manager looks at before and after an offer goes out. */
const buildChecks = (offer) => {
  const letter = hasOfferLetter(offer);
  const checks = [
    {
      label: "All template fields filled",
      state: !letter ? "warn" : hasUnfilledPlaceholders(offer) ? "warn" : "ok",
      value: !letter ? "Letter not generated yet" : hasUnfilledPlaceholders(offer) ? "Some {{fields}} are not filled" : "Yes",
    },
    {
      label: "Compensation",
      state: "info",
      value: `${offer.compensation || "Not set"} · Job budget: ${jobBudgetLabel(offer.job) || "Not set"}`,
    },
  ];

  if (offer.status === OFFER_STATUS.PENDING_APPROVAL) {
    checks.push({ label: "Manager approval", state: "warn", value: "Pending" });
  } else if (offer.status === OFFER_STATUS.DRAFT) {
    checks.push(offer.rejection_reason
      ? { label: "Manager approval", state: "warn", value: `Rejected${offer.reviewed_by_name ? ` by ${offer.reviewed_by_name}` : ""}: ${offer.rejection_reason}` }
      : { label: "Manager approval", state: "info", value: "Not submitted yet" });
  } else {
    const by = offer.reviewed_by_name ? ` by ${offer.reviewed_by_name}` : "";
    const on = offer.reviewed_at ? ` on ${formatDateTime(offer.reviewed_at)}` : "";
    checks.push({ label: "Manager approval", state: "ok", value: `Approved${by}${on}` });
  }

  checks.push({
    label: "Candidate stage",
    state: "info",
    value: offer.profile?.detailed_status ? String(offer.profile.detailed_status).replace(/_/g, " ") : "—",
  });
  return checks;
};

/**
 * Manager's read-only view of one offer (/offers/:offerId): status, the
 * generated letter as the candidate receives it, and checks. Decisions are
 * made in Approvals; nothing can be changed here.
 */
const OfferDetails = () => {
  const { offerId } = useParams();
  const { data: offers = [], isLoading, error } = useOffers();
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const offer = offers.find((o) => String(o.offer_id) === String(offerId));

  useEffect(() => {
    if (!isLoading && !error && !offer) logger.warn(`[Offers] Offer ${offerId} not found in the manager's offers.`);
  }, [isLoading, error, offer, offerId]);

  const downloadDocs = async () => {
    setDownloading(true);
    setDownloadError("");
    logger.info(`[Offers] Downloading documents of offer ${offerId}.`);
    try {
      const response = await API.get(`/api/offers/${offerId}/download-docs`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "documents.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error(`[Offers] Download of offer ${offerId} documents failed — ${err?.message}`);
      setDownloadError("Could not download the documents. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const breadcrumb = (name) => (
    <Breadcrumbs sx={{ mb: 1 }}>
      <Link component={RouterLink} to="/offers" underline="hover">Offers</Link>
      <Typography color="text.primary">{name}</Typography>
    </Breadcrumbs>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error || !offer) {
    return (
      <Box sx={{ p: 3 }}>
        {breadcrumb("Offer")}
        <Alert severity={error ? "error" : "info"}>
          {error ? "Could not load the offer. Please try again." : "This offer was not found. It may have been removed."}
        </Alert>
      </Box>
    );
  }

  const candidate = offer.profile?.candidate_name || "Candidate";
  const docs = offerDocsState(offer);

  return (
    <Box sx={{ p: 3 }}>
      {breadcrumb(candidate)}
      <Typography variant="h5" fontWeight={700}>Offer</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {[candidate, [offer.job?.job_code, offer.job?.title].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
      </Typography>

      <Alert severity={offer.status === OFFER_STATUS.ACCEPTED ? "success" : offer.status === OFFER_STATUS.DECLINED ? "error" : "info"} sx={{ mb: 2 }}>
        {offerStatusSummary(offer)}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Offer letter</Typography>
            {hasOfferLetter(offer) ? (
              // The letter is HTML written from a template: shown sandboxed (no
              // scripts) and as white paper, exactly as the candidate gets it.
              <Box
                component="iframe"
                title={`Offer letter for ${candidate}`}
                sandbox=""
                srcDoc={offer.offer_text}
                sx={{ width: "100%", height: 480, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "#fff" }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">Letter not generated yet.</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Checks</Typography>
            <Stack spacing={1.5}>
              {buildChecks(offer).map((check) => (
                <Stack key={check.label} direction="row" spacing={1} alignItems="flex-start">
                  {CHECK_ICONS[check.state]}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{check.label}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>{check.value}</Typography>
                  </Box>
                </Stack>
              ))}

              {docs !== "none" && (
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  {CHECK_ICONS[docs === "pending" ? "warn" : "ok"]}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Documents</Typography>
                    {docs === "pending" && <Typography variant="body2" color="text.secondary">Pending</Typography>}
                    {docs === "download" && (
                      <Button size="small" variant="outlined" sx={{ mt: 0.5 }} disabled={downloading} onClick={downloadDocs}>
                        {downloading ? "Downloading..." : "Download Docs"}
                      </Button>
                    )}
                    {docs === "link" && (
                      <Button size="small" variant="outlined" sx={{ mt: 0.5 }} href={offer.candidate_message_id} target="_blank" rel="noopener noreferrer">
                        Download Docs
                      </Button>
                    )}
                    {downloadError && <Typography variant="caption" color="error" display="block">{downloadError}</Typography>}
                  </Box>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OfferDetails;
